// Stage 6: parse the SOAP response into a typed result.
//
// Two operations parse to different shapes:
//   - parseFilingResponse → FilingReceipt (ReviewFiling)
//   - parseFeesResponse  → FeeBreakdown  (GetFeesCalculation)
//
// Stages are exposed as factory functions so the orchestrator picks the right
// one based on the operation. Logger calls (formerly via pino) are now
// structured events emitted via ctx.onEvent.
import { XMLParser } from 'fast-xml-parser';
import { emit } from '../../pipeline/trace.js';
const filingXmlParser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    removeNSPrefix: true,
});
const feesXmlParser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    removeNSPrefix: true,
    isArray: name => ['AllowanceCharge'].includes(name),
});
// ---------------------------------------------------------------------------
// Filing response stage (ReviewFiling)
// ---------------------------------------------------------------------------
export const parseFilingResponseStage = async (input, ctx) => {
    emit(ctx.onEvent, {
        traceId: ctx.traceId,
        stage: 'parse',
        level: 'debug',
        event: 'filing_response_raw',
        data: { xmlLength: input.xml.length, preview: input.xml.substring(0, 3000) },
    });
    return parseFilingResponse(input.xml, ctx);
};
Object.defineProperty(parseFilingResponseStage, 'stageName', { value: 'parse' });
/**
 * Build a non-confirmed FilingReceipt (clean-rejection or in-doubt). Keeps
 * externalFilingId '' and status 'error' so the no-false-positive invariant
 * holds by construction and the api-gateway's transitional 502 guard still
 * fires.
 */
function unconfirmedReceipt(outcome, statusMessage) {
    return {
        outcome,
        externalFilingId: '',
        status: 'error',
        timestamp: new Date().toISOString(),
        statusMessage,
    };
}
/**
 * Classify a ReviewFilingResponse onto a SubmissionOutcome (PR #726
 * no-false-positive preserved). The structural failures that previously threw
 * are now classified RETURNS, keeping the same telemetry events:
 *   - non-zero Error.ErrorCode business rejection → 'clean-rejection' (R2).
 *   - missing Envelope/Body, a SOAP fault, missing response message, or a
 *     Success carrying no DocumentFileControlID → 'in-doubt' (R3).
 *   - Success WITH a non-empty filing id → 'confirmed-success' (R1); the ONLY
 *     path that sets a non-empty externalFilingId.
 * The >5 MB size guard (in stage-5 transport) and transport/timeout errors are
 * not parser-visible — the submit service maps those to in-doubt in its catch.
 */
export function parseFilingResponse(xml, ctx) {
    const parsed = filingXmlParser.parse(xml);
    const body = parsed?.Envelope?.Body;
    if (!body) {
        const fault = parsed?.Envelope?.Body?.Fault ||
            parsed?.['SOAP-ENV:Envelope']?.['SOAP-ENV:Body']?.['SOAP-ENV:Fault'];
        if (fault) {
            const faultString = fault.faultstring || fault.Reason?.Text || JSON.stringify(fault);
            if (ctx) {
                emit(ctx.onEvent, {
                    traceId: ctx.traceId,
                    stage: 'parse',
                    level: 'error',
                    event: 'filing_soap_fault',
                    data: { fault: faultString },
                });
            }
            return unconfirmedReceipt('in-doubt', `JTI SOAP fault: ${faultString}`);
        }
        return unconfirmedReceipt('in-doubt', 'Invalid ReviewFiling response: missing Envelope/Body');
    }
    const message = body.ReviewFilingResponseMessage || body.MessageReceiptMessage || body.ReviewFilingResponse;
    if (!message) {
        if (ctx) {
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'parse',
                level: 'error',
                event: 'filing_response_unknown_message',
                data: { bodyKeys: Object.keys(body) },
            });
        }
        return unconfirmedReceipt('in-doubt', 'Invalid ReviewFiling response: missing response message');
    }
    // JTI returns <Error><ErrorCode>0</ErrorCode><ErrorText>No Error</ErrorText></Error> on SUCCESS.
    // Only treat as rejection when errorCode is non-zero.
    const errorInfo = message.Error;
    if (errorInfo) {
        const errorCode = String(errorInfo.ErrorCode || '');
        const errorText = typeof errorInfo.ErrorText === 'string'
            ? errorInfo.ErrorText
            : JSON.stringify(errorInfo.ErrorText);
        const isRealError = errorCode !== '0' && errorCode !== '';
        if (isRealError) {
            if (ctx) {
                emit(ctx.onEvent, {
                    traceId: ctx.traceId,
                    stage: 'parse',
                    level: 'error',
                    event: 'filing_rejected_by_efm',
                    data: { errorCode, errorText },
                });
            }
            return unconfirmedReceipt('clean-rejection', `Filing rejected by court: ${errorText}`);
        }
    }
    const externalFilingId = extractFilingId(message);
    if (!externalFilingId) {
        if (ctx) {
            emit(ctx.onEvent, {
                traceId: ctx.traceId,
                stage: 'parse',
                level: 'error',
                event: 'filing_response_no_id',
                data: { messageKeys: Object.keys(message) },
            });
        }
        return unconfirmedReceipt('in-doubt', 'Invalid ReviewFiling response: missing DocumentFileControlID');
    }
    return {
        outcome: 'confirmed-success',
        externalFilingId,
        status: extractStatus(message),
        timestamp: extractTimestamp(message) || new Date().toISOString(),
        statusMessage: extractStatusMessage(message),
    };
}
function extractFilingId(message) {
    const paths = [
        () => getStringValue(message, 'DocumentFileControlID'),
        () => getStringValue(message.DocumentIdentification || {}, 'IdentificationID'),
        () => {
            const cfm = message.CoreFilingMessage;
            if (!cfm)
                return null;
            return (getStringValue(cfm, 'DocumentFileControlID') ||
                getStringValue(cfm.DocumentIdentification || {}, 'IdentificationID'));
        },
    ];
    for (const path of paths) {
        const value = path();
        if (value)
            return value;
    }
    return null;
}
function extractStatus(message) {
    const statusCode = getStringValue(message, 'StatusCode') || getStringValue(message, 'FilingStatusCode') || '';
    if (statusCode.toLowerCase().includes('error') || statusCode.toLowerCase().includes('fail')) {
        return 'error';
    }
    return 'received';
}
function extractStatusMessage(message) {
    return (getStringValue(message, 'StatusText') || getStringValue(message, 'StatusDescriptionText') || undefined);
}
function extractTimestamp(message) {
    return getStringValue(message, 'DateTime') || getStringValue(message, 'DocumentFiledDate') || null;
}
function getStringValue(obj, key) {
    const value = obj[key];
    if (typeof value === 'string')
        return value;
    if (value && typeof value === 'object') {
        const nested = value;
        if (typeof nested['#text'] === 'string')
            return nested['#text'];
        if (typeof nested['IdentificationID'] === 'string')
            return nested['IdentificationID'];
        if (typeof nested['DateTime'] === 'string')
            return nested['DateTime'];
    }
    return null;
}
// ---------------------------------------------------------------------------
// Fees response stage (GetFeesCalculation)
// ---------------------------------------------------------------------------
export const parseFeesResponseStage = async (input, ctx) => {
    emit(ctx.onEvent, {
        traceId: ctx.traceId,
        stage: 'parse',
        level: 'debug',
        event: 'fees_response_raw',
        data: { xmlLength: input.xml.length },
    });
    return parseFeesResponse(input.xml);
};
Object.defineProperty(parseFeesResponseStage, 'stageName', { value: 'parse' });
export function parseFeesResponse(xml) {
    const parsed = feesXmlParser.parse(xml);
    const body = parsed?.Envelope?.Body;
    if (!body) {
        throw new Error('Invalid GetFeesCalculation response: missing Envelope/Body');
    }
    const message = body.FeesCalculationResponseMessage || body;
    const error = message.Error;
    if (error && typeof error === 'object') {
        const errorCode = getFeeTextValue(error, 'ErrorCode');
        const errorText = getFeeTextValue(error, 'ErrorText');
        if (errorCode && errorCode !== '0') {
            throw new Error(`EFM fee calculation error [${errorCode}]: ${errorText || 'Unknown error'}`);
        }
    }
    const totalAmountStr = getFeeTextValue(message, 'FeesCalculationAmount');
    const totalAmount = totalAmountStr ? parseFloat(totalAmountStr) : 0;
    const charges = message.AllowanceCharge;
    const lineItems = [];
    if (charges && Array.isArray(charges)) {
        for (const charge of charges) {
            if (!charge || typeof charge !== 'object')
                continue;
            const c = charge;
            const description = getFeeTextValue(c, 'AllowanceChargeReason') || 'Fee';
            const amountStr = getFeeTextValue(c, 'Amount') || '0';
            const amount = parseFloat(amountStr);
            const costCode = getFeeTextValue(c, 'AccountingCostCode') || '';
            const category = costCode === 'JOURNAL' ? 'JOURNAL' : 'COURT';
            lineItems.push({ description, amount, category });
        }
    }
    const computedTotal = lineItems.length > 0 && totalAmount === 0
        ? lineItems.reduce((sum, item) => sum + item.amount, 0)
        : totalAmount;
    return { totalAmount: computedTotal, lineItems };
}
function getFeeTextValue(obj, key) {
    const value = obj[key];
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return String(value);
    if (value && typeof value === 'object') {
        const nested = value;
        if (typeof nested['#text'] === 'string')
            return nested['#text'];
        if (typeof nested['Amount'] === 'string')
            return nested['Amount'];
    }
    return null;
}
